import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Bell, BellRing, Eye, CheckCircle2, RotateCcw, Building2, Calendar, Clock, History, Download } from "lucide-react";
import { RelationRenderer } from "@/components/office/RelationRenderer";

async function downloadOfficePdf(instanceId) {
  try {
    const res = await api.get(`/office/relations/instances/${instanceId}/pdf`, { responseType: "blob" });
    const cd = res.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = m ? m[1] : "relazione.pdf"; document.body.appendChild(a); a.click();
    a.remove(); window.URL.revokeObjectURL(url);
  } catch (e) { toast.error("Impossibile generare il PDF"); }
}

function fmtDate(s) {
  if (!s) return "—";
  const d = new Date(s.length <= 10 ? `${s}T00:00:00` : s);
  return isNaN(d) ? s : d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}
const STATUS_LABEL = { da_compilare: "Da compilare", in_bozza: "In bozza", consegnata: "Consegnata", revisione_richiesta: "Revisione richiesta", approvata: "Approvata", senza_operatore: "Senza operatore" };
const STATUS_CLS = {
  da_compilare: "bg-slate-100 text-slate-600 border-slate-200", in_bozza: "bg-amber-50 text-amber-700 border-amber-200",
  consegnata: "bg-sky-50 text-sky-700 border-sky-200", revisione_richiesta: "bg-orange-50 text-orange-700 border-orange-200",
  approvata: "bg-emerald-50 text-emerald-700 border-emerald-200", senza_operatore: "bg-slate-50 text-slate-400 border-slate-200",
};
const CHIPS = [["all", "Tutte"], ["da_compilare", "Da compilare"], ["in_bozza", "In bozza"], ["consegnata", "Consegnate"], ["revisione_richiesta", "Revisione richiesta"], ["approvata", "Approvate"], ["senza_operatore", "Senza operatore"]];

function StatusBadge({ s }) {
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${STATUS_CLS[s] || STATUS_CLS.da_compilare}`}>{STATUS_LABEL[s] || s}</span>;
}

function ReadDialog({ instanceId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [revOpen, setRevOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [note, setNote] = useState(""); const [due, setDue] = useState("");
  const [ver, setVer] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get(`/office/relations/instances/${instanceId}`); setD(r.data); }
    catch (e) { toast.error(formatApiError(e)); onClose(); }
  }, [instanceId, onClose]);
  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setBusy(true);
    try { await api.post(`/office/relations/instances/${instanceId}/approve`); toast.success("Relazione approvata"); setApproveOpen(false); onChanged && onChanged(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const requestRevision = async () => {
    if (!note.trim() || !due) { toast.error("Nota e nuova scadenza obbligatorie"); return; }
    setBusy(true);
    try { await api.post(`/office/relations/instances/${instanceId}/request-revision`, { note: note.trim(), due_date: due }); toast.success("Modifica richiesta"); setRevOpen(false); onChanged && onChanged(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const openVersion = async (vid) => {
    try { const r = await api.get(`/office/relations/instances/${instanceId}/versions/${vid}`); setVer(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const canAct = d && d.status === "consegnata";
  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="office-read-dialog">
          <DialogHeader><DialogTitle>{d?.title || "Relazione"}</DialogTitle></DialogHeader>
          {!d ? <p className="text-sm text-slate-500 p-4">Caricamento…</p> : (
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 space-y-1">
                <p><span className="font-semibold">Utente:</span> {d.user_name} · <StatusBadge s={d.status} /></p>
                {d.submitted_by_name && <p><span className="font-semibold">Consegnata da:</span> {d.submitted_by_name} il {fmtDate(d.submitted_at)}</p>}
                {d.approved_by_name && <p><span className="font-semibold">Approvata da:</span> {d.approved_by_name} il {fmtDate(d.approved_at)} (v{d.approved_version_number})</p>}
                <p><span className="font-semibold">Scadenza:</span> {fmtDate(d.due_date)}{d.is_expired && d.status !== "approvata" ? " · Scaduta" : ""}</p>
              </div>
              {d.versions && d.versions.length > 1 && (
                <div className="rounded-lg border border-slate-200 p-3" data-testid="office-versions">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Versioni precedenti</p>
                  <div className="space-y-1.5">
                    {d.versions.filter((v) => v.version_number !== d.current_version_number).map((v) => (
                      <button key={v.version_id} onClick={() => openVersion(v.version_id)} className="w-full text-left text-xs rounded-md border border-slate-200 px-2.5 py-1.5 hover:bg-slate-50" data-testid={`office-version-${v.version_number}`}>
                        Versione {v.version_number} · {fmtDate(v.submitted_at)} · {v.submitted_by_name || "—"}{v.revision_note ? ` · Rev: ${v.revision_note}` : ""}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <RelationRenderer title={null} description={d.description} fields={d.fields || []} values={d.answers || {}} readOnly />
            </div>
          )}
          {canAct && (
            <DialogFooter>
              <Button variant="outline" onClick={() => { setNote(""); setDue(""); setRevOpen(true); }} data-testid="office-request-revision-btn"><RotateCcw className="h-4 w-4 mr-1.5" /> Richiedi modifica</Button>
              <Button onClick={() => setApproveOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="office-approve-btn"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Approva</Button>
            </DialogFooter>
          )}
          {d && d.status === "approvata" && (
            <DialogFooter>
              <Button variant="outline" onClick={() => downloadOfficePdf(instanceId)} data-testid="office-pdf-btn"><Download className="h-4 w-4 mr-1.5" /> Scarica PDF</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={revOpen} onOpenChange={setRevOpen}>
        <DialogContent data-testid="office-revision-dialog">
          <DialogHeader><DialogTitle>Richiedi modifica</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-slate-500">Motivazione *</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Cosa integrare/correggere" data-testid="office-revision-note" /></div>
            <div><Label className="text-xs text-slate-500">Nuova scadenza *</Label><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="office-revision-due" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevOpen(false)}>Annulla</Button>
            <Button onClick={requestRevision} disabled={busy} data-testid="office-revision-confirm">Invia richiesta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={approveOpen} onOpenChange={setApproveOpen}>
        <AlertDialogContent data-testid="office-approve-dialog">
          <AlertDialogHeader><AlertDialogTitle>Approvare la relazione?</AlertDialogTitle>
            <AlertDialogDescription>Questa versione diventerà quella ufficiale approvata e non sarà più modificabile.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={approve} className="bg-emerald-600 hover:bg-emerald-700" data-testid="office-approve-confirm">Approva</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!ver} onOpenChange={(o) => !o && setVer(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="office-version-dialog">
          <DialogHeader><DialogTitle>Versione {ver?.version_number}</DialogTitle></DialogHeader>
          {ver && <div className="rounded-lg bg-slate-50 border border-slate-200 p-4"><RelationRenderer title={null} description={ver.description} fields={ver.fields || []} values={ver.answers || {}} readOnly /></div>}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function RelationsMonitor({ templateId, onClose }) {
  const [pid, setPid] = useState(null);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [readId, setReadId] = useState(null);
  const [remindAll, setRemindAll] = useState(null);
  const [confirmRemind, setConfirmRemind] = useState(null);

  useEffect(() => {
    api.get(`/office/relations/templates/${templateId}`).then((r) => setPid(r.data.publication_id)).catch((e) => { toast.error(formatApiError(e)); onClose(); });
  }, [templateId, onClose]);

  const load = useCallback(async () => {
    if (!pid) return;
    try { const r = await api.get(`/office/relations/publications/${pid}/monitor?status=${status}&q=${encodeURIComponent(q)}&page=${page}&page_size=50`); setData(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [pid, status, q, page]);
  useEffect(() => { load(); }, [load]);

  const remind = async (iid, force = false) => {
    try {
      const r = await api.post(`/office/relations/instances/${iid}/remind`, { force });
      if (r.data.needs_confirm) { setConfirmRemind({ iid, message: r.data.message }); return; }
      toast.success("Operatore sollecitato"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openRemindAll = async () => {
    try { const r = await api.get(`/office/relations/publications/${pid}/remind-all-summary`); setRemindAll(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doRemindAll = async () => {
    try { const r = await api.post(`/office/relations/publications/${pid}/remind-all`); toast.success(`Sollecitati ${r.data.operators} operatori · ${r.data.reminded} relazioni`); setRemindAll(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const p = data?.publication;
  const c = data?.counts || {};
  const canRemind = (row) => row.worker_name && ["da_compilare", "in_bozza", "revisione_richiesta"].includes(row.status);
  const canRead = (row) => ["consegnata", "revisione_richiesta", "approvata"].includes(row.status);

  return (
    <div className="space-y-4" data-testid="relations-monitor">
      <button onClick={onClose} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline" data-testid="monitor-back">
        <ArrowLeft className="h-4 w-4" /> Torna all'elenco
      </button>
      {p && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4">
          <h2 className="font-heading font-bold text-slate-900">{p.title}</h2>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
            <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {p.appalto_nome} · {p.service_label}</span>
            {p.period_ref && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {p.period_ref}</span>}
            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Scad. {fmtDate(p.due_date)}</span>
            <span>Pubblicata il {fmtDate(p.published_at)}</span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="monitor-chips">
        {CHIPS.map(([k, l]) => (
          <button key={k} onClick={() => { setStatus(k); setPage(1); }}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${status === k ? "bg-[#3A4A7A] text-white border-[#3A4A7A]" : "bg-white text-slate-600 border-slate-200 hover:border-[#8478C4]"}`}
            data-testid={`monitor-chip-${k}`}>{l} <span className="font-semibold">{c[k] ?? 0}</span></button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Cerca utente o operatore…" className="max-w-xs" data-testid="monitor-search" />
        <Button variant="outline" onClick={openRemindAll} className="ml-auto" data-testid="monitor-remind-all"><BellRing className="h-4 w-4 mr-1.5" /> Sollecita tutti i mancanti</Button>
      </div>

      <div className="rounded-xl border border-slate-200 overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left font-medium p-2">Utente</th>
              <th className="text-left font-medium p-2">Operatore titolare</th>
              <th className="text-left font-medium p-2">Stato</th>
              <th className="text-left font-medium p-2">Scadenza</th>
              <th className="text-left font-medium p-2">Ultimo agg.</th>
              <th className="text-right font-medium p-2">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items || []).length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400" data-testid="monitor-empty">Nessuna relazione per questo filtro.</td></tr>}
            {(data?.items || []).map((row) => (
              <tr key={row.instance_id} className="border-t border-slate-100" data-testid={`monitor-row-${row.instance_id}`}>
                <td className="p-2 font-medium text-slate-700">{row.user_name}</td>
                <td className="p-2 text-slate-600">{row.worker_name || <span className="text-slate-400">—</span>}</td>
                <td className="p-2"><StatusBadge s={row.display_status} />{row.is_expired && row.status !== "approvata" && <span className="ml-1.5 text-xs font-semibold text-red-600">Scaduta</span>}</td>
                <td className="p-2 text-slate-600">{fmtDate(row.due_date)}</td>
                <td className="p-2 text-slate-500 text-xs">{row.updated_at ? fmtDate(row.updated_at) : "—"}{row.last_reminder_at ? <div className="text-[11px] text-slate-400">Sollec. {fmtDate(row.last_reminder_at)}</div> : null}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  {canRead(row) && <Button size="sm" variant="outline" onClick={() => setReadId(row.instance_id)} data-testid={`monitor-read-${row.instance_id}`} className="mr-1"><Eye className="h-3.5 w-3.5 mr-1" /> {row.status === "revisione_richiesta" ? "Visualizza" : "Leggi"}</Button>}
                  {canRemind(row) && <Button size="sm" variant="ghost" onClick={() => remind(row.instance_id)} data-testid={`monitor-remind-${row.instance_id}`}><Bell className="h-3.5 w-3.5 mr-1" /> Sollecita</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.total > data.page_size && (
        <div className="flex items-center justify-center gap-3 text-sm">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((x) => x - 1)}>Precedente</Button>
          <span className="text-slate-500">Pagina {page} di {Math.ceil(data.total / data.page_size)}</span>
          <Button size="sm" variant="outline" disabled={page >= Math.ceil(data.total / data.page_size)} onClick={() => setPage((x) => x + 1)}>Successiva</Button>
        </div>
      )}

      {readId && <ReadDialog instanceId={readId} onClose={() => setReadId(null)} onChanged={load} />}

      <AlertDialog open={!!remindAll} onOpenChange={(o) => !o && setRemindAll(null)}>
        <AlertDialogContent data-testid="monitor-remind-all-dialog">
          <AlertDialogHeader><AlertDialogTitle>Sollecita tutti i mancanti</AlertDialogTitle>
            <AlertDialogDescription>Verranno sollecitati {remindAll?.operators} operatori per {remindAll?.relations} relazioni.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doRemindAll} data-testid="monitor-remind-all-confirm">Invia solleciti</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmRemind} onOpenChange={(o) => !o && setConfirmRemind(null)}>
        <AlertDialogContent data-testid="monitor-remind-confirm-dialog">
          <AlertDialogHeader><AlertDialogTitle>Inviare comunque?</AlertDialogTitle>
            <AlertDialogDescription>{confirmRemind?.message}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={() => { const iid = confirmRemind.iid; setConfirmRemind(null); remind(iid, true); }} data-testid="monitor-remind-confirm-yes">Invia comunque</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default RelationsMonitor;
